import { trackEvent, trackModuleCompleted, trackTechniqueSession } from "../lib/events/sdk";
import type { PracticeReceipt } from '../lib/practice/protocols';
import { syncService } from '../lib/syncService';
import { ownerStorage } from '../lib/privacy/ownerStorage';
import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from 'react';
import { checkAchievements, type AchievementProgress } from '../data/achievements';
import { SHOP_ITEMS, getShopItem, type ShopItem } from '../data/shopItems';

// Types
interface StressEntry {
  date: string;
  symptoms: string[];
  customSymptom?: string; // User-entered custom symptom text
  fuelBefore: number;
  fuelAfter: number;
  reflection?: string;
}

interface QuizStats {
  totalQuestions: number;
  correctAnswers: number;
}

interface DailyChallenge {
  id: string;
  completedDate?: string;
}

interface MoodCheckIn {
  date: string;
  mood: 'stressed' | 'okay' | 'good';
  timestamp: string;
}

interface TechniqueSession {
  date: string;
  technique: string;
  duration: number;
  preStress?: number;
  postStress?: number;
}

interface WeeklyChallengeReward {
  challengeId: string;
  weekStart: string;
  claimedAt: string;
}

// Goal types for structured goal-setting
type GoalType = 'academic' | 'skill' | 'wellness' | 'social' | 'personal';

interface Milestone {
  id: string;
  title: string;
  completed: boolean;
  completedAt?: string;
  points: number;
}

interface Goal {
  id: string;
  title: string;
  description?: string;
  type: GoalType;
  targetDate?: string;
  milestones: Milestone[];
  status: 'active' | 'completed' | 'paused';
  createdAt: string;
  completedAt?: string;
  reflection?: string;
  linkedChallenge?: string;
}

// Module progress tracking for spaced repetition
interface ModuleProgressLog {
  moduleId: string;
  completedAt: string; // ISO timestamp of initial completion
  lastRefresherAt?: string; // ISO timestamp of last refresher
  refreshersCompleted: number;
  quizAccuracy: number; // Last quiz accuracy for this module (0-100)
}

// Assessment result tracking for pre/post module questions
export interface AssessmentResult {
  moduleId: string;
  type: 'pre' | 'post';
  score: number; // 0-100
  totalQuestions: number;
  correctAnswers: number;
  completedAt: string; // ISO timestamp
}

// Journal entry types
interface JournalEntry {
  id: string;
  date: string;
  timestamp: string;
  text: string;
  type: 'personal' | 'reflection' | 'vent';
  mood?: 'stressed' | 'okay' | 'good';
  prompt?: string; // Optional prompt that inspired this entry
}

// Shop purchase record
interface ShopPurchase {
  itemId: string;
  price: number;
  purchasedAt: string;
}

// ============================================
// PSYCHOLOGICAL FRAMEWORK TYPES
// ============================================

// SRL (Self-Regulated Learning) tracking
type SRLActivityType = 'module' | 'awu' | 'technique' | 'challenge' | 'brain-gym';

interface SRLForethought {
  goal: string;
  strategy: string;
  confidenceLevel: 1 | 2 | 3 | 4 | 5;
}

interface SRLPerformanceMonitoring {
  strategySwitches: number;
  checkpointResponses: string[];
}

interface SRLReflection {
  goalMet: 'yes' | 'partially' | 'no';
  whatWorked: string;
  whatToChange: string;
}

interface SRLSession {
  id: string;
  activityType: SRLActivityType;
  activityId: string;
  timestamp: string;
  forethought: SRLForethought | null;
  performanceMonitoring: SRLPerformanceMonitoring;
  reflection: SRLReflection | null;
  completedFullCycle: boolean;
}

// Metacognition strategy tracking
interface StrategyUsage {
  strategyId: string;
  activityId: string;
  timestamp: string;
  outcome: 'effective' | 'neutral' | 'ineffective';
}

// Executive Function game sessions
interface EFGameSession {
  gameId: string;
  timestamp: string;
  score: number;
  level: number;
  accuracy: number;
  reactionTimeMs: number;
  personalBest: boolean;
}

// ZPD skill profile
type SkillDimension = 'stress-management' | 'growth-mindset' | 'metacognition' | 'self-regulation' | 'executive-function' | 'coping-skills';

interface SkillProfile {
  skillId: SkillDimension;
  currentLevel: number;   // 1-10 computed from performance
  lastAssessed: string;
  history: { date: string; level: number }[];
}

// Scaffold level type
type ScaffoldLevel = 'full' | 'guided' | 'independent';

// Autonomy choice log entry
interface AutonomyChoice {
  timestamp: string;
  choice: string;
  context: string;
}

// Community anonymous aggregates (local cache)
interface CommunityPulse {
  lastFetched: string;
  totalUsersThisWeek: number;
  topTechnique: string;
  avgStreakLength: number;
  communityBrainPoints: number;
  techniqueHelpfulness: Record<string, number>;
}

// Custom daily practice
interface CustomPractice {
  id: string;
  activities: { type: string; id: string; label: string }[];
  createdAt: string;
  lastCompleted?: string;
}

export interface AccessibleGameReceipt {id:string;gameId:string;completedAt:string;correct:number;total:number;mode:'untimed-text'}
interface UserProgress {
  accessibleGameSessions?: AccessibleGameReceipt[];
  rewardDay?: string;
  rewardedPointsToday?: number;
  practiceReceipts?: PracticeReceipt[];
  level: number;
  brainPoints: number;
  streak: number;
  lastActiveDate: string;
  completedModules: string[];
  completedAWU: number;
  achievements: string[];
  // New fields for enhanced features
  stressHistory: StressEntry[];
  reflections: Array<{ date: string; text: string }>;
  // Personal journal entries
  journalEntries: JournalEntry[];
  quizStats: QuizStats;
  fuelLevel: number;
  streakFreezeUsedThisWeek: boolean;
  lastStreakFreezeWeek: string;
  dailyChallenges: DailyChallenge[];
  // Daily mood check-ins
  moodCheckIns: MoodCheckIn[];
  // Technique usage tracking
  techniqueSessions: TechniqueSession[];
  // Unlocked themes
  unlockedThemes: string[];
  // Weekly challenge rewards
  claimedWeeklyChallenges: WeeklyChallengeReward[];
  // Engagement depth tracking
  engagementLog: EngagementEntry[];
  // User's saved custom symptoms for reuse
  savedCustomSymptoms: string[];
  // User goals for growth tracking
  goals: Goal[];
  // Per-module progress for spaced repetition
  moduleProgress: ModuleProgressLog[];
  // Rewards shop inventory and purchases
  inventory: Record<string, number>;
  purchaseHistory: ShopPurchase[];
  // Pre/post assessment results for measuring growth
  assessmentResults: AssessmentResult[];
  // === Psychological Framework Data ===
  // SRL cycle tracking
  srlSessions: SRLSession[];
  // Metacognition strategy tracking
  strategyUsage: StrategyUsage[];
  preferredStrategies: string[];
  // Executive function game data
  efGameSessions: EFGameSession[];
  efPersonalBests: Record<string, number>; // gameId -> best score
  // Skill profile for ZPD
  skillProfiles: SkillProfile[];
  // Scaffold levels per activity type
  scaffoldLevels: Record<string, ScaffoldLevel>;
  // Autonomy choices log
  autonomyChoices: AutonomyChoice[];
  // Community pulse cache
  communityPulse: CommunityPulse | null;
  // Custom daily practice
  customPractice: CustomPractice | null;
}

// User intent - why they're using the app (growth-oriented framing)
type UserIntent =
  | 'build-skills'      // Proactive: building mental resilience
  | 'program-participant' // Part of school/organization program
  | 'prepare-ahead'     // Want to be ready for future challenges
  | 'manage-stress'     // Currently dealing with stress
  | 'support-others'    // Want to help friends/peers
  | 'just-curious';     // Exploring what resilience is about

// Stressor types - only relevant if user intent is 'manage-stress'
type PrimaryStressor = 'test-anxiety' | 'study-overwhelm' | 'presentation' | 'grade-pressure' | 'general';

interface UserProfile {
  handle: string;
  gradeLevel: string;
  // User's reason for being here
  userIntent?: UserIntent;
  // Enhanced onboarding fields (optional, based on intent)
  primaryStressor?: PrimaryStressor;
  stressTriggers: string[];
  commonSymptoms: string[];
  baselineStress: number;
  upcomingEvent?: {
    name: string;
    date: string;
  };
  hardestSubject?: string;
  onboardingCompletedAt?: string;
}

// Progress Monitor Types
export type StressTrend = 'improving' | 'stable' | 'increasing' | 'insufficient_data';
export type EngagementLevel = 'new' | 'returning' | 'consistent' | 'declining' | 'exploring';

export interface InteractionEntry {
  type: 'mood_checkin' | 'technique_used' | 'awu_session' | 'module_completed' | 'reflection_written';
  timestamp: string;
  context: {
    mood?: 'stressed' | 'okay' | 'good';
    technique?: string;
    duration?: number;
    preStress?: number;
    postStress?: number;
    hourOfDay: number;
  };
}

// Engagement depth tracking for verified activities
export interface EngagementEntry {
  type: string;
  timestamp: string;
  data: Record<string, unknown>;
}

export interface InteractionLog {
  entries: InteractionEntry[];
  lastUpdated: string;
}

export interface UserInsights {
  preferredCheckInTime?: { hour: number; dayOfWeek?: number };
  averageSessionDuration: number;
  mostEffectiveTechnique?: string;
  stressTrend: StressTrend;
  engagementLevel: EngagementLevel;
  daysUntilNextEvent?: number;
  eventPreparedness: 'not_started' | 'in_progress' | 'ready';
  lastComputed: string;
  daysSinceActive: number;
}

export interface SmartNudge {
  id: string;
  type: 'event_prep' | 'reengagement' | 'follow_up' | 'technique_suggest' | 'celebration' | 'streak_at_risk';
  priority: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  action?: {
    label: string;
    route: string;
  };
  dismissable: boolean;
  triggeredBy: string;
  createdAt: string;
}

export interface RecommendedAction {
  id: string;
  priority: number;
  type: 'quick_action' | 'suggested_module' | 'technique' | 'reflection';
  title: string;
  reason: string;
  route: string;
  icon: string;
}

interface AppState {
  user: UserProfile;
  progress: UserProgress;
  settings: {
    rewardsEnabled?: boolean;
    reducedMotion: boolean;
    soundEnabled: boolean;
    theme: 'dark' | 'system' | 'ocean' | 'forest' | 'sunset';
    // Accessibility settings
    fontSize: 'default' | 'large' | 'x-large';
    highContrast: boolean;
    enhancedFocus: boolean;
    textToSpeech: boolean;
    // Organizational settings
    cohortCode?: string;
    shareWithCounselor?: boolean;
    parentDigest?: boolean;
  };
  onboardingComplete: boolean;
  // Feature tour state
  featureTourComplete: boolean;
  featureTourDismissed: boolean;
}

type AppAction =
  | {type:'LOG_ACCESSIBLE_GAME'; session:AccessibleGameReceipt}
  | { type: 'LOG_PRIVATE_PRACTICE'; receipt: PracticeReceipt }
  | { type: 'ANSWER_PRACTICE_FOLLOWUP'; id: string; day: 3 | 7; used: boolean }
  | { type: 'EARN_POINTS'; amount: number }
  | { type: 'COMPLETE_MODULE'; moduleId: string; points: number }
  | { type: 'COMPLETE_AWU'; points: number }
  | { type: 'UNLOCK_ACHIEVEMENT'; achievementId: string }
  | { type: 'UPDATE_SETTINGS'; settings: Partial<AppState['settings']> }
  | { type: 'UPDATE_STREAK' }
  | { type: 'COMPLETE_ONBOARDING'; profile: UserProfile }
  | { type: 'RESET_PROGRESS' }
  | { type: 'LOAD_STATE'; state: AppState }
  | { type: 'LOG_STRESS'; entry: StressEntry }
  | { type: 'ADD_REFLECTION'; text: string }
  | { type: 'ADD_JOURNAL_ENTRY'; entry: Omit<JournalEntry, 'id' | 'date' | 'timestamp'> }
  | { type: 'UPDATE_QUIZ_STATS'; correct: boolean }
  | { type: 'SET_FUEL_LEVEL'; level: number }
  | { type: 'USE_STREAK_FREEZE' }
  | { type: 'COMPLETE_DAILY_CHALLENGE'; challengeId: string }
  | { type: 'LOG_MOOD_CHECKIN'; mood: 'stressed' | 'okay' | 'good' }
  | { type: 'LOG_TECHNIQUE_SESSION'; session: TechniqueSession }
  | { type: 'UNLOCK_THEME'; themeId: string }
  | { type: 'CLAIM_WEEKLY_CHALLENGE'; challengeId: string; reward: number }
  | { type: 'LOG_ENGAGEMENT'; entryType: string; data: Record<string, unknown> }
  | { type: 'CREATE_GOAL'; goal: Goal }
  | { type: 'UPDATE_GOAL'; goalId: string; updates: Partial<Goal> }
  | { type: 'DELETE_GOAL'; goalId: string }
  | { type: 'COMPLETE_MILESTONE'; goalId: string; milestoneId: string }
  | { type: 'COMPLETE_GOAL'; goalId: string; reflection?: string }
  | { type: 'PAUSE_GOAL'; goalId: string }
  | { type: 'RESUME_GOAL'; goalId: string }
  | { type: 'LOG_MODULE_COMPLETION'; moduleId: string }
  | { type: 'COMPLETE_REFRESHER'; moduleId: string; quizAccuracy?: number }
  | { type: 'PURCHASE_ITEM'; itemId: string; price: number }
  | { type: 'USE_INVENTORY_ITEM'; itemId: string }
  | { type: 'LOG_ASSESSMENT_RESULT'; result: AssessmentResult }
  // === Psychological Framework Actions ===
  | { type: 'LOG_SRL_SESSION'; session: SRLSession }
  | { type: 'LOG_STRATEGY_USAGE'; usage: StrategyUsage }
  | { type: 'UPDATE_PREFERRED_STRATEGIES'; strategies: string[] }
  | { type: 'LOG_EF_GAME'; session: EFGameSession }
  | { type: 'UPDATE_SKILL_PROFILE'; skillId: SkillDimension; level: number }
  | { type: 'UPDATE_SCAFFOLD_LEVEL'; activityType: string; level: ScaffoldLevel }
  | { type: 'LOG_AUTONOMY_CHOICE'; choice: string; context: string }
  | { type: 'UPDATE_COMMUNITY_PULSE'; pulse: CommunityPulse }
  | { type: 'SET_CUSTOM_PRACTICE'; practice: CustomPractice | null }
  // Feature tour actions
  | { type: 'COMPLETE_FEATURE_TOUR' }
  | { type: 'DISMISS_FEATURE_TOUR' }
  | { type: 'RESET_FEATURE_TOUR' };

// Initial state - fresh start for new users
export const initialState: AppState = {
  user: {
    handle: 'NewExplorer',
    gradeLevel: '9th',
    userIntent: undefined,
    stressTriggers: [],
    commonSymptoms: [],
    baselineStress: 5,
  },
  progress: {
    level: 1,
    brainPoints: 0,
    streak: 0,
    lastActiveDate: '',
    completedModules: [],
    completedAWU: 0,
    achievements: [],
    stressHistory: [],
    reflections: [],
    journalEntries: [],
    quizStats: { totalQuestions: 0, correctAnswers: 0 },
    fuelLevel: 100,
    streakFreezeUsedThisWeek: false,
    lastStreakFreezeWeek: '',
    dailyChallenges: [],
    moodCheckIns: [],
    techniqueSessions: [],
    unlockedThemes: ['dark', 'system'], // Default themes available
    claimedWeeklyChallenges: [],
    engagementLog: [],
    savedCustomSymptoms: [],
    goals: [],
    moduleProgress: [],
    inventory: {},
    purchaseHistory: [],
    assessmentResults: [],
    // Psychological framework defaults
    srlSessions: [],
    strategyUsage: [],
    preferredStrategies: [],
    efGameSessions: [],
    efPersonalBests: {},
    skillProfiles: [],
    scaffoldLevels: {},
    autonomyChoices: [],
    communityPulse: null,
    customPractice: null,
  },
  settings: {
    reducedMotion: false,
    soundEnabled: true,
    theme: 'dark',
    fontSize: 'default',
    highContrast: false,
    enhancedFocus: false,
    textToSpeech: false,
  },
  onboardingComplete: false,
  featureTourComplete: false,
  featureTourDismissed: false,
};

// Level thresholds
const LEVEL_THRESHOLDS = [
  0, 100, 250, 500, 800, 1200, 1700, 2300, 3000, 4000, 5000,
];

function calculateLevel(points: number): number {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (points >= LEVEL_THRESHOLDS[i]) return i + 1;
  }
  return 1;
}

// Reducer
export function appReducer(state: AppState, action: AppAction): AppState {
  const next=reduceState(state,action);
  if(next===state || action.type==='LOAD_STATE' || action.type==='RESET_PROGRESS') return next;
  const delta=next.progress.brainPoints-state.progress.brainPoints;
  if(delta<=0) return next;
  const day=new Date().toISOString().slice(0,10);const used=state.progress.rewardDay===day?state.progress.rewardedPointsToday||0:0;
  const award=state.settings.rewardsEnabled===false?0:Math.max(0,Math.min(Math.floor(delta),150-used));
  const brainPoints=state.progress.brainPoints+award;
  return {...next,progress:{...next.progress,brainPoints,level:calculateLevel(brainPoints),rewardDay:day,rewardedPointsToday:used+award}};
}
function reduceState(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'LOG_ACCESSIBLE_GAME': {const sessions=state.progress.accessibleGameSessions||[];if(sessions.some(s=>s.id===action.session.id)) return state;return {...state,progress:{...state.progress,accessibleGameSessions:[...sessions,action.session]}};}
    case 'LOG_PRIVATE_PRACTICE': {
      const receipts=state.progress.practiceReceipts||[];
      if(receipts.some(r=>r.id===action.receipt.id)) return state;
      const today=action.receipt.completedAt.slice(0,10);
      const rewarded=receipts.filter(r=>r.status==='completed' && r.completedAt.startsWith(today)).length;
      const reward=action.receipt.status==='completed' && rewarded<3 ? 10 : 0;
      const brainPoints=state.progress.brainPoints+reward;
      return {...state,progress:{...state.progress,practiceReceipts:[...receipts,action.receipt],brainPoints,level:calculateLevel(brainPoints)}};
    }
    case 'ANSWER_PRACTICE_FOLLOWUP': return {...state,progress:{...state.progress,practiceReceipts:(state.progress.practiceReceipts||[]).map(r=>r.id===action.id?{...r,followUps:r.followUps.map(f=>f.day===action.day&&!f.answeredAt?{...f,answeredAt:new Date().toISOString(),used:action.used}:f)}:r)}};

    case 'EARN_POINTS': {
      const newPoints = state.progress.brainPoints + action.amount;
      const newLevel = calculateLevel(newPoints);
      return {
        ...state,
        progress: {
          ...state.progress,
          brainPoints: newPoints,
          level: newLevel,
        },
      };
    }

    case 'COMPLETE_MODULE': {
      if (state.progress.completedModules.includes(action.moduleId)) {
        return state;
      }
      const newPoints = state.progress.brainPoints + action.points;
      return {
        ...state,
        progress: {
          ...state.progress,
          brainPoints: newPoints,
          level: calculateLevel(newPoints),
          completedModules: [...state.progress.completedModules, action.moduleId],
        },
      };
    }

    case 'COMPLETE_AWU': {
      const newPoints = state.progress.brainPoints + action.points;
      return {
        ...state,
        progress: {
          ...state.progress,
          brainPoints: newPoints,
          level: calculateLevel(newPoints),
          completedAWU: state.progress.completedAWU + 1,
        },
      };
    }

    case 'UNLOCK_ACHIEVEMENT': {
      if (state.progress.achievements.includes(action.achievementId)) {
        return state;
      }
      return {
        ...state,
        progress: {
          ...state.progress,
          achievements: [...state.progress.achievements, action.achievementId],
        },
      };
    }

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.settings },
      };

    case 'UPDATE_STREAK': {
      const today = new Date().toISOString().split('T')[0];
      const lastActive = state.progress.lastActiveDate;
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

      if (lastActive === today) return state;

      const newStreak = lastActive === yesterday ? state.progress.streak + 1 : 1;
      return {
        ...state,
        progress: {
          ...state.progress,
          streak: newStreak,
          lastActiveDate: today,
        },
      };
    }

    case 'COMPLETE_ONBOARDING':
      if(state.onboardingComplete) return state;
      return {
        ...state,
        user: {
          ...action.profile,
          onboardingCompletedAt: new Date().toISOString(),
        },
        onboardingComplete: true,
        progress: {
          ...state.progress,
          brainPoints: 35, // Welcome bonus, independent of optional breathing
          level: 1,
          lastActiveDate: new Date().toISOString().split('T')[0],
          streak: 1,
        },
      };

    case 'RESET_PROGRESS':
      return initialState;

    case 'LOAD_STATE':
      return action.state;

    case 'LOG_STRESS': {
      // If there's a custom symptom, save it for future reuse
      let updatedCustomSymptoms = state.progress.savedCustomSymptoms || [];
      if (action.entry.customSymptom && action.entry.customSymptom.trim()) {
        const customText = action.entry.customSymptom.trim();
        // Add only if not already saved (case-insensitive check)
        const alreadyExists = updatedCustomSymptoms.some(
          s => s.toLowerCase() === customText.toLowerCase()
        );
        if (!alreadyExists) {
          // Keep max 10 custom symptoms, most recent at end
          updatedCustomSymptoms = [...updatedCustomSymptoms, customText].slice(-10);
        }
      }
      return {
        ...state,
        progress: {
          ...state.progress,
          stressHistory: [...state.progress.stressHistory, action.entry],
          savedCustomSymptoms: updatedCustomSymptoms,
        },
      };
    }

    case 'ADD_REFLECTION': {
      const today = new Date().toISOString().split('T')[0];
      return {
        ...state,
        progress: {
          ...state.progress,
          reflections: [...state.progress.reflections, { date: today, text: action.text }],
        },
      };
    }

    case 'ADD_JOURNAL_ENTRY': {
      const now = new Date();
      const entry: JournalEntry = {
        id: `journal_${now.getTime()}_${Math.random().toString(36).substr(2, 9)}`,
        date: now.toISOString().split('T')[0],
        timestamp: now.toISOString(),
        ...action.entry,
      };
      return {
        ...state,
        progress: {
          ...state.progress,
          journalEntries: [...(state.progress.journalEntries || []), entry],
        },
      };
    }

    case 'UPDATE_QUIZ_STATS': {
      return {
        ...state,
        progress: {
          ...state.progress,
          quizStats: {
            totalQuestions: state.progress.quizStats.totalQuestions + 1,
            correctAnswers: state.progress.quizStats.correctAnswers + (action.correct ? 1 : 0),
          },
        },
      };
    }

    case 'SET_FUEL_LEVEL': {
      return {
        ...state,
        progress: {
          ...state.progress,
          fuelLevel: Math.max(0, Math.min(100, action.level)),
        },
      };
    }

    case 'USE_STREAK_FREEZE': {
      const currentWeek = getWeekNumber(new Date());
      if (state.progress.streakFreezeUsedThisWeek && state.progress.lastStreakFreezeWeek === currentWeek) {
        return state; // Already used this week
      }
      return {
        ...state,
        progress: {
          ...state.progress,
          streakFreezeUsedThisWeek: true,
          lastStreakFreezeWeek: currentWeek,
        },
      };
    }

    case 'COMPLETE_DAILY_CHALLENGE': {
      const today = new Date().toISOString().split('T')[0];
      const existing = state.progress.dailyChallenges.find(c => c.id === action.challengeId);
      if (existing?.completedDate === today) return state; // Already completed today

      const updated = state.progress.dailyChallenges.filter(c => c.id !== action.challengeId);
      return {
        ...state,
        progress: {
          ...state.progress,
          dailyChallenges: [...updated, { id: action.challengeId, completedDate: today }],
        },
      };
    }

    case 'LOG_MOOD_CHECKIN': {
      const today = new Date().toISOString().split('T')[0];
      const timestamp = new Date().toISOString();
      // Remove any existing check-in for today
      const filtered = state.progress.moodCheckIns.filter(c => c.date !== today);
      return {
        ...state,
        progress: {
          ...state.progress,
          moodCheckIns: [...filtered, { date: today, mood: action.mood, timestamp }],
        },
      };
    }

    case 'LOG_TECHNIQUE_SESSION': {
      return {
        ...state,
        progress: {
          ...state.progress,
          techniqueSessions: [...state.progress.techniqueSessions, action.session],
        },
      };
    }

    case 'UNLOCK_THEME': {
      if (state.progress.unlockedThemes?.includes(action.themeId)) {
        return state;
      }
      return {
        ...state,
        progress: {
          ...state.progress,
          unlockedThemes: [...(state.progress.unlockedThemes || ['dark', 'system']), action.themeId],
        },
      };
    }

    case 'CLAIM_WEEKLY_CHALLENGE': {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - dayOfWeek);
      weekStart.setHours(0, 0, 0, 0);
      const weekStartStr = weekStart.toISOString().split('T')[0];

      // Check if already claimed this week
      const alreadyClaimed = state.progress.claimedWeeklyChallenges?.some(
        c => c.challengeId === action.challengeId && c.weekStart === weekStartStr
      );
      if (alreadyClaimed) return state;

      const newPoints = state.progress.brainPoints + action.reward;
      return {
        ...state,
        progress: {
          ...state.progress,
          brainPoints: newPoints,
          level: calculateLevel(newPoints),
          claimedWeeklyChallenges: [
            ...(state.progress.claimedWeeklyChallenges || []),
            {
              challengeId: action.challengeId,
              weekStart: weekStartStr,
              claimedAt: new Date().toISOString(),
            },
          ],
        },
      };
    }

    case 'LOG_ENGAGEMENT': {
      const entry: EngagementEntry = {
        type: action.entryType,
        timestamp: new Date().toISOString(),
        data: action.data,
      };
      return {
        ...state,
        progress: {
          ...state.progress,
          engagementLog: [...(state.progress.engagementLog || []), entry],
        },
      };
    }

    case 'CREATE_GOAL': {
      return {
        ...state,
        progress: {
          ...state.progress,
          goals: [...(state.progress.goals || []), action.goal],
        },
      };
    }

    case 'UPDATE_GOAL': {
      const goals = (state.progress.goals || []).map(goal =>
        goal.id === action.goalId ? { ...goal, ...action.updates } : goal
      );
      return {
        ...state,
        progress: {
          ...state.progress,
          goals,
        },
      };
    }

    case 'DELETE_GOAL': {
      const goals = (state.progress.goals || []).filter(goal => goal.id !== action.goalId);
      return {
        ...state,
        progress: {
          ...state.progress,
          goals,
        },
      };
    }

    case 'COMPLETE_MILESTONE': {
      const goals = (state.progress.goals || []).map(goal => {
        if (goal.id !== action.goalId) return goal;
        const milestones = goal.milestones.map(m =>
          m.id === action.milestoneId
            ? { ...m, completed: true, completedAt: new Date().toISOString() }
            : m
        );
        return { ...goal, milestones };
      });
      return {
        ...state,
        progress: {
          ...state.progress,
          goals,
        },
      };
    }

    case 'COMPLETE_GOAL': {
      const goals = (state.progress.goals || []).map(goal => {
        if (goal.id !== action.goalId) return goal;
        return {
          ...goal,
          status: 'completed' as const,
          completedAt: new Date().toISOString(),
          reflection: action.reflection,
        };
      });
      return {
        ...state,
        progress: {
          ...state.progress,
          goals,
        },
      };
    }

    case 'PAUSE_GOAL': {
      const goals = (state.progress.goals || []).map(goal =>
        goal.id === action.goalId ? { ...goal, status: 'paused' as const } : goal
      );
      return {
        ...state,
        progress: {
          ...state.progress,
          goals,
        },
      };
    }

    case 'RESUME_GOAL': {
      const goals = (state.progress.goals || []).map(goal =>
        goal.id === action.goalId ? { ...goal, status: 'active' as const } : goal
      );
      return {
        ...state,
        progress: {
          ...state.progress,
          goals,
        },
      };
    }

    case 'LOG_MODULE_COMPLETION': {
      const now = new Date().toISOString();
      const existing = (state.progress.moduleProgress || []).find(
        p => p.moduleId === action.moduleId
      );
      if (existing) {
        // Module already tracked - just update completedAt
        const moduleProgress = state.progress.moduleProgress.map(p =>
          p.moduleId === action.moduleId
            ? { ...p, completedAt: now }
            : p
        );
        return {
          ...state,
          progress: { ...state.progress, moduleProgress },
        };
      }
      // First time - create entry
      const newEntry: ModuleProgressLog = {
        moduleId: action.moduleId,
        completedAt: now,
        refreshersCompleted: 0,
        quizAccuracy: 0,
      };
      return {
        ...state,
        progress: {
          ...state.progress,
          moduleProgress: [...(state.progress.moduleProgress || []), newEntry],
        },
      };
    }

    case 'COMPLETE_REFRESHER': {
      const now = new Date().toISOString();
      const moduleProgress = (state.progress.moduleProgress || []).map(p =>
        p.moduleId === action.moduleId
          ? {
              ...p,
              refreshersCompleted: p.refreshersCompleted + 1,
              lastRefresherAt: now,
              quizAccuracy: action.quizAccuracy ?? p.quizAccuracy,
            }
          : p
      );
      return {
        ...state,
        progress: { ...state.progress, moduleProgress },
      };
    }

    case 'PURCHASE_ITEM': {
      if (state.progress.brainPoints < action.price) return state;
      const newPoints = state.progress.brainPoints - action.price;
      const currentQty = (state.progress.inventory || {})[action.itemId] || 0;
      const purchase: ShopPurchase = {
        itemId: action.itemId,
        price: action.price,
        purchasedAt: new Date().toISOString(),
      };
      return {
        ...state,
        progress: {
          ...state.progress,
          brainPoints: newPoints,
          level: calculateLevel(newPoints),
          inventory: {
            ...(state.progress.inventory || {}),
            [action.itemId]: currentQty + 1,
          },
          purchaseHistory: [...(state.progress.purchaseHistory || []), purchase],
        },
      };
    }

    case 'USE_INVENTORY_ITEM': {
      const inventory = state.progress.inventory || {};
      const qty = inventory[action.itemId] || 0;
      if (qty <= 0) return state;
      return {
        ...state,
        progress: {
          ...state.progress,
          inventory: {
            ...inventory,
            [action.itemId]: qty - 1,
          },
        },
      };
    }

    case 'LOG_ASSESSMENT_RESULT': {
      return {
        ...state,
        progress: {
          ...state.progress,
          assessmentResults: [
            ...(state.progress.assessmentResults || []),
            action.result,
          ],
        },
      };
    }

    // === Psychological Framework Reducers ===

    case 'LOG_SRL_SESSION': {
      return {
        ...state,
        progress: {
          ...state.progress,
          srlSessions: [...(state.progress.srlSessions || []), action.session],
        },
      };
    }

    case 'LOG_STRATEGY_USAGE': {
      return {
        ...state,
        progress: {
          ...state.progress,
          strategyUsage: [...(state.progress.strategyUsage || []), action.usage],
        },
      };
    }

    case 'UPDATE_PREFERRED_STRATEGIES': {
      return {
        ...state,
        progress: {
          ...state.progress,
          preferredStrategies: action.strategies,
        },
      };
    }

    case 'LOG_EF_GAME': {
      const currentBest = (state.progress.efPersonalBests || {})[action.session.gameId] ?? 0;
      const isNewBest = action.session.score > currentBest;
      return {
        ...state,
        progress: {
          ...state.progress,
          efGameSessions: [...(state.progress.efGameSessions || []), {
            ...action.session,
            personalBest: isNewBest,
          }],
          efPersonalBests: isNewBest
            ? { ...(state.progress.efPersonalBests || {}), [action.session.gameId]: action.session.score }
            : (state.progress.efPersonalBests || {}),
        },
      };
    }

    case 'UPDATE_SKILL_PROFILE': {
      const existingProfiles = state.progress.skillProfiles || [];
      const now = new Date().toISOString();
      const existing = existingProfiles.find(p => p.skillId === action.skillId);
      if (existing) {
        const updatedProfiles = existingProfiles.map(p =>
          p.skillId === action.skillId
            ? {
                ...p,
                currentLevel: action.level,
                lastAssessed: now,
                history: [...p.history, { date: now, level: action.level }].slice(-30),
              }
            : p
        );
        return {
          ...state,
          progress: { ...state.progress, skillProfiles: updatedProfiles },
        };
      }
      return {
        ...state,
        progress: {
          ...state.progress,
          skillProfiles: [...existingProfiles, {
            skillId: action.skillId,
            currentLevel: action.level,
            lastAssessed: now,
            history: [{ date: now, level: action.level }],
          }],
        },
      };
    }

    case 'UPDATE_SCAFFOLD_LEVEL': {
      return {
        ...state,
        progress: {
          ...state.progress,
          scaffoldLevels: {
            ...(state.progress.scaffoldLevels || {}),
            [action.activityType]: action.level,
          },
        },
      };
    }

    case 'LOG_AUTONOMY_CHOICE': {
      const entry: AutonomyChoice = {
        timestamp: new Date().toISOString(),
        choice: action.choice,
        context: action.context,
      };
      return {
        ...state,
        progress: {
          ...state.progress,
          autonomyChoices: [...(state.progress.autonomyChoices || []), entry].slice(-100),
        },
      };
    }

    case 'UPDATE_COMMUNITY_PULSE': {
      return {
        ...state,
        progress: {
          ...state.progress,
          communityPulse: action.pulse,
        },
      };
    }

    case 'SET_CUSTOM_PRACTICE': {
      return {
        ...state,
        progress: {
          ...state.progress,
          customPractice: action.practice,
        },
      };
    }

    // Feature tour reducers
    case 'COMPLETE_FEATURE_TOUR':
      return {
        ...state,
        featureTourComplete: true,
        featureTourDismissed: false,
      };

    case 'DISMISS_FEATURE_TOUR':
      return {
        ...state,
        featureTourComplete: false,
        featureTourDismissed: true,
      };

    case 'RESET_FEATURE_TOUR':
      return {
        ...state,
        featureTourComplete: false,
        featureTourDismissed: false,
      };

    default:
      return state;
  }
}

// Helper to get ISO week number
function getWeekNumber(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${weekNo}`;
}

// Achievement notification callback type
type AchievementNotifyCallback = (achievement: { id: string; label: string; icon: string; points: number }) => void;

// Context
interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  earnPoints: (amount: number) => void;
  completeModule: (moduleId: string, points?: number) => void;
  completeAWU: (points?: number) => void;
  getNextLevelThreshold: () => number;
  getProgressToNextLevel: () => number;
  checkIn: () => boolean; // Returns true if check-in earned points
  hasCheckedInToday: () => boolean;
  completeOnboarding: (profile: UserProfile) => void;
  resetProgress: () => void;
  unlockAchievement: (achievementId: string) => void;
  setAchievementNotifyCallback: (callback: AchievementNotifyCallback | null) => void;
  checkAndUnlockAchievements: () => void;
  // New enhanced features
  logStress: (entry: StressEntry) => void;
  addReflection: (text: string) => void;
  updateQuizStats: (correct: boolean) => void;
  setFuelLevel: (level: number) => void;
  useStreakFreeze: () => boolean; // Returns true if freeze was used
  canUseStreakFreeze: () => boolean;
  completeDailyChallenge: (challengeId: string) => void;
  isDailyChallengeCompleted: (challengeId: string) => boolean;
  getQuizAccuracy: () => number;
  // Mood check-in features
  logMoodCheckIn: (mood: 'stressed' | 'okay' | 'good') => void;
  hasMoodCheckedInToday: () => boolean;
  getTodaysMood: () => 'stressed' | 'okay' | 'good' | null;
  // Technique tracking
  logTechniqueSession: (technique: string, duration: number, preStress?: number, postStress?: number) => void;
  getTechniqueStats: () => { technique: string; count: number; avgImprovement: number }[];
  getTopTechnique: () => { technique: string; count: number; avgImprovement: number } | null;
  // Weekly challenges
  getWeeklyChallengeProgress: () => {
    techniquesUsed: number;
    awuCompleted: number;
    modulesCompleted: number;
    moodCheckIns: number;
    weekStart: string;
  };
  // Theme unlocking
  unlockTheme: (themeId: string) => void;
  isThemeUnlocked: (themeId: string) => boolean;
  checkAndUnlockThemes: () => void;
  getAvailableThemes: () => { id: string; name: string; unlocked: boolean; unlockCondition: string }[];
  // Weekly challenge rewards
  claimWeeklyChallenge: (challengeId: string, reward: number) => void;
  isWeeklyChallengeClaimed: (challengeId: string) => boolean;
  // Engagement depth tracking
  logEngagement: (type: string, data: Record<string, unknown>) => void;
  getEngagementStats: () => {
    totalReflections: number;
    verifiedReflections: number;
    avgReflectionLength: number;
    moodCheckInsWithContext: number;
  };
  // Custom symptoms
  getSavedCustomSymptoms: () => string[];
  // Goal management
  createGoal: (title: string, type: GoalType, milestones: string[], description?: string, targetDate?: string) => void;
  updateGoal: (goalId: string, updates: Partial<Goal>) => void;
  deleteGoal: (goalId: string) => void;
  completeMilestone: (goalId: string, milestoneId: string) => void;
  completeGoal: (goalId: string, reflection?: string) => void;
  pauseGoal: (goalId: string) => void;
  resumeGoal: (goalId: string) => void;
  getActiveGoals: () => Goal[];
  getCompletedGoals: () => Goal[];
  getGoalProgress: (goalId: string) => { completed: number; total: number; percentage: number };
  getGoalSuggestions: () => { title: string; type: GoalType; milestones: string[] }[];
  // Personal journaling
  addJournalEntry: (entry: Omit<JournalEntry, 'id' | 'date' | 'timestamp'>) => void;
  getJournalEntries: () => JournalEntry[];
  getRecentJournalEntries: (limit?: number) => JournalEntry[];
  hasJournaledToday: () => boolean;
  // Spaced repetition / module progress
  logModuleCompletion: (moduleId: string) => void;
  completeRefresher: (moduleId: string, quizAccuracy?: number) => void;
  getModuleProgress: () => ModuleProgressLog[];
  // Rewards shop
  purchaseShopItem: (itemId: string) => boolean;
  useInventoryItem: (itemId: string) => boolean;
  getInventoryCount: (itemId: string) => number;
  canPurchase: (itemId: string) => boolean;
  isItemOwned: (itemId: string) => boolean;
  getShopItems: () => ShopItem[];
  getTotalSpent: () => number;
  // Assessment tracking
  logAssessmentResult: (result: AssessmentResult) => void;
  getAssessmentResults: (moduleId?: string) => AssessmentResult[];
  getModuleGrowth: (moduleId: string) => { preScore: number | null; postScore: number | null; growth: number | null };
  getOverallGrowth: () => { avgPreScore: number; avgPostScore: number; avgGrowth: number; modulesAssessed: number };
  // === Psychological Framework Helpers ===
  // SRL
  logSRLSession: (session: SRLSession) => void;
  getSRLSessions: () => SRLSession[];
  getSRLCompletionRate: () => number;
  // Metacognition
  logStrategyUsage: (usage: StrategyUsage) => void;
  getStrategyEffectiveness: () => { strategyId: string; uses: number; effectiveRate: number }[];
  updatePreferredStrategies: (strategies: string[]) => void;
  // Executive Functions
  logEFGame: (session: Omit<EFGameSession, 'personalBest'>) => void;
  getEFPersonalBests: () => Record<string, number>;
  getEFGameHistory: (gameId: string) => EFGameSession[];
  getAllEFGameSessions: () => EFGameSession[];
  // ZPD / Scaffolding
  updateSkillProfile: (skillId: SkillDimension, level: number) => void;
  getSkillProfiles: () => SkillProfile[];
  getScaffoldLevel: (activityType: string) => ScaffoldLevel;
  updateScaffoldLevel: (activityType: string, level: ScaffoldLevel) => void;
  // SDT
  logAutonomyChoice: (choice: string, context: string) => void;
  updateCommunityPulse: (pulse: CommunityPulse) => void;
  getCommunityPulse: () => CommunityPulse | null;
  setCustomPractice: (practice: CustomPractice | null) => void;
  getCustomPractice: () => CustomPractice | null;
  // Feature tour helpers
  completeFeatureTour: () => void;
  dismissFeatureTour: () => void;
  resetFeatureTour: () => void;
  shouldShowFeatureTour: () => boolean;
}

const AppContext = createContext<AppContextValue | null>(null);

// Storage key
const STORAGE_KEY = 'resilience_forge_app_state';

// Provider
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState, () => {
    const saved = ownerStorage.getItem(STORAGE_KEY);
    if (!saved) return initialState;
    try {
      const parsed = JSON.parse(saved) as AppState;
      if (!parsed.user || !parsed.progress || !parsed.settings || !Array.isArray(parsed.progress.completedModules)) throw new Error('Invalid saved state');
      return { ...initialState, ...parsed, progress: { ...initialState.progress, ...parsed.progress }, settings: { ...initialState.settings, ...parsed.settings } };
    } catch { ownerStorage.setItem('recovery-original', saved); return initialState; }
  });
  const [achievementCallback, setAchievementCallback] = useState<AchievementNotifyCallback | null>(null);

  useEffect(() => {
    ownerStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    const timer = window.setTimeout(() => void syncService.saveSnapshot(state as unknown as Record<string, unknown>), 1000);
    return () => window.clearTimeout(timer);
  }, [state]);



  // Set achievement notification callback
  const setAchievementNotifyCallback = useCallback((callback: AchievementNotifyCallback | null) => {
    setAchievementCallback(() => callback);
  }, []);

  // Check and unlock achievements
  const checkAndUnlockAchievements = useCallback(() => {
    // Calculate unique techniques used
    const uniqueTechniques = new Set(state.progress.techniqueSessions.map(s => s.technique)).size;

    // Calculate stress improvements
    const stressImproved = state.progress.techniqueSessions.filter(
      s => s.preStress !== undefined && s.postStress !== undefined && s.postStress < s.preStress
    ).length;

    // Calculate goal statistics
    const goals = state.progress.goals || [];
    const goalsCreated = goals.length;
    const goalsCompleted = goals.filter(g => g.status === 'completed').length;
    const milestonesCompleted = goals.reduce(
      (sum, g) => sum + g.milestones.filter(m => m.completed).length,
      0
    );
    const goalReflectionsWritten = goals.filter(
      g => g.status === 'completed' && g.reflection && g.reflection.trim().length > 0
    ).length;
    const uniqueGoalTypes = new Set(
      goals.filter(g => g.status === 'completed').map(g => g.type)
    ).size;

    // Calculate EF game statistics
    const efSessions = state.progress.efGameSessions || [];
    const efGamesPlayed = efSessions.length;
    const efUniqueGamesPlayed = new Set(efSessions.map(s => s.gameId)).size;
    const efPersonalBestsCount = Object.keys(state.progress.efPersonalBests || {}).length;

    // Calculate SRL statistics
    const srlSessions = state.progress.srlSessions || [];
    const srlFullCycles = srlSessions.filter(s => s.completedFullCycle).length;
    const srlTotalSessions = srlSessions.length;

    const achievementProgress: AchievementProgress = {
      completedAWU: state.progress.completedAWU,
      completedModules: state.progress.completedModules,
      streak: state.progress.streak,
      level: state.progress.level,
      brainPoints: state.progress.brainPoints,
      // Enhanced tracking
      moodCheckIns: state.progress.moodCheckIns.length,
      techniqueSessions: state.progress.techniqueSessions.length,
      uniqueTechniques,
      stressImproved,
      // Goal tracking
      goalsCreated,
      goalsCompleted,
      milestonesCompleted,
      goalReflectionsWritten,
      uniqueGoalTypes,
      // Brain Gym / EF tracking
      efGamesPlayed,
      efUniqueGamesPlayed,
      efPersonalBestsCount,
      // SRL tracking
      srlFullCycles,
      srlTotalSessions,
    };

    const newAchievements = checkAchievements(achievementProgress, state.progress.achievements);

    for (const achievement of newAchievements) {
      dispatch({ type: 'UNLOCK_ACHIEVEMENT', achievementId: achievement.id });
      dispatch({ type: 'EARN_POINTS', amount: achievement.points });

      if (achievementCallback) {
        achievementCallback({
          id: achievement.id,
          label: achievement.label,
          icon: achievement.icon,
          points: achievement.points,
        });
      }
    }
  }, [state.progress, achievementCallback]);

  // Check achievements when progress changes
  useEffect(() => {
    if (state.onboardingComplete) {
      checkAndUnlockAchievements();
    }
  }, [
    state.progress.completedAWU,
    state.progress.completedModules.length,
    state.progress.streak,
    state.progress.level,
    state.progress.moodCheckIns.length,
    state.progress.techniqueSessions.length,
    state.progress.goals,
    state.progress.efGameSessions?.length,
    state.progress.srlSessions?.length,
    state.onboardingComplete,
  ]);

  // Helper functions
  const earnPoints = (amount: number) => {
    dispatch({ type: 'EARN_POINTS', amount });
  };

  const completeModule = (moduleId: string, points = 50) => {
    if (!state.progress.completedModules.includes(moduleId)) void trackModuleCompleted(moduleId, moduleId);
    dispatch({ type: 'COMPLETE_MODULE', moduleId, points });
  };

  const completeAWU = (points = 30) => {
    dispatch({ type: 'COMPLETE_AWU', points });
  };

  const getNextLevelThreshold = () => {
    const currentLevel = state.progress.level;
    if (currentLevel >= LEVEL_THRESHOLDS.length) {
      return LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
    }
    return LEVEL_THRESHOLDS[currentLevel];
  };

  const getProgressToNextLevel = () => {
    const currentPoints = state.progress.brainPoints;
    const currentThreshold = LEVEL_THRESHOLDS[state.progress.level - 1] || 0;
    const nextThreshold = getNextLevelThreshold();
    const range = nextThreshold - currentThreshold;
    const progress = currentPoints - currentThreshold;
    return range > 0 ? (progress / range) * 100 : 100;
  };

  const hasCheckedInToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return state.progress.lastActiveDate === today;
  };

  const checkIn = () => {
    if (hasCheckedInToday()) return false;
    dispatch({ type: 'UPDATE_STREAK' });
    dispatch({ type: 'EARN_POINTS', amount: 10 });
    return true;
  };

  const completeOnboarding = (profile: UserProfile) => {
    dispatch({ type: 'COMPLETE_ONBOARDING', profile });
  };

  const resetProgress = () => {
    dispatch({ type: 'RESET_PROGRESS' });
  };

  const unlockAchievement = (achievementId: string) => {
    if (!state.progress.achievements.includes(achievementId)) {
      dispatch({ type: 'UNLOCK_ACHIEVEMENT', achievementId });
    }
  };

  // New enhanced feature helpers
  const logStress = (entry: StressEntry) => {
    dispatch({ type: 'LOG_STRESS', entry });
  };

  const addReflection = (text: string) => {
    dispatch({ type: 'ADD_REFLECTION', text });

  };

  const updateQuizStats = (correct: boolean) => {
    dispatch({ type: 'UPDATE_QUIZ_STATS', correct });
  };

  const setFuelLevel = (level: number) => {
    dispatch({ type: 'SET_FUEL_LEVEL', level });
  };

  const canUseStreakFreeze = () => {
    const currentWeek = getWeekNumber(new Date());
    return !(state.progress.streakFreezeUsedThisWeek && state.progress.lastStreakFreezeWeek === currentWeek);
  };

  const useStreakFreeze = () => {
    if (!canUseStreakFreeze()) return false;
    dispatch({ type: 'USE_STREAK_FREEZE' });
    return true;
  };

  const completeDailyChallenge = (challengeId: string) => {
    dispatch({ type: 'COMPLETE_DAILY_CHALLENGE', challengeId });
    dispatch({ type: 'EARN_POINTS', amount: 20 }); // Daily challenge bonus
  };

  const isDailyChallengeCompleted = (challengeId: string) => {
    const today = new Date().toISOString().split('T')[0];
    const challenge = state.progress.dailyChallenges.find(c => c.id === challengeId);
    return challenge?.completedDate === today;
  };

  const getQuizAccuracy = () => {
    const { totalQuestions, correctAnswers } = state.progress.quizStats;
    return totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  };

  // Mood check-in helpers
  const hasMoodCheckedInToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return state.progress.moodCheckIns.some(c => c.date === today);
  };

  const getTodaysMood = (): 'stressed' | 'okay' | 'good' | null => {
    const today = new Date().toISOString().split('T')[0];
    const todaysCheckIn = state.progress.moodCheckIns.find(c => c.date === today);
    return todaysCheckIn?.mood ?? null;
  };

  const logMoodCheckIn = (mood: 'stressed' | 'okay' | 'good') => {
    dispatch({ type: 'LOG_MOOD_CHECKIN', mood });
    dispatch({ type: 'EARN_POINTS', amount: 5 }); // 5 FP for mood check-in
    // Also update streak if not already done today
    if (!hasCheckedInToday()) {
      dispatch({ type: 'UPDATE_STREAK' });
      dispatch({ type: 'EARN_POINTS', amount: 5 }); // Additional 5 FP for daily streak
    }
  };

  // Technique tracking helpers
  const logTechniqueSession = (technique: string, duration: number, preStress?: number, postStress?: number) => {
    const session: TechniqueSession = {
      date: new Date().toISOString(),
      technique,
      duration,
      preStress,
      postStress,
    };
    dispatch({ type: 'LOG_TECHNIQUE_SESSION', session });
    void trackTechniqueSession(technique, technique, Math.max(0, duration) * 1000);
  };

  const getTechniqueStats = () => {
    const techniques = new Map<string, { count: number; improvements: number[] }>();

    for (const session of state.progress.techniqueSessions) {
      const existing = techniques.get(session.technique) ?? { count: 0, improvements: [] };
      existing.count++;
      if (session.preStress !== undefined && session.postStress !== undefined) {
        existing.improvements.push(session.preStress - session.postStress);
      }
      techniques.set(session.technique, existing);
    }

    return Array.from(techniques.entries()).map(([technique, data]) => ({
      technique,
      count: data.count,
      avgImprovement: data.improvements.length > 0
        ? data.improvements.reduce((a, b) => a + b, 0) / data.improvements.length
        : 0,
    }));
  };

  const getTopTechnique = () => {
    const stats = getTechniqueStats();
    if (stats.length === 0) return null;
    // Sort by most used, then by best improvement
    return stats.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return b.avgImprovement - a.avgImprovement;
    })[0];
  };

  // Get weekly challenge progress (current week)
  const getWeeklyChallengeProgress = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - dayOfWeek);
    weekStart.setHours(0, 0, 0, 0);
    const weekStartStr = weekStart.toISOString().split('T')[0];

    // Filter sessions/check-ins from this week
    const weekSessions = state.progress.techniqueSessions.filter(s =>
      new Date(s.date) >= weekStart
    );
    const weekMoodCheckIns = state.progress.moodCheckIns.filter(c =>
      new Date(c.date) >= weekStart
    );

    // Unique techniques used this week
    const uniqueTechniques = new Set(weekSessions.map(s => s.technique)).size;

    return {
      techniquesUsed: uniqueTechniques,
      awuCompleted: state.progress.completedAWU, // Total AWU (could track weekly if needed)
      modulesCompleted: state.progress.completedModules.length,
      moodCheckIns: weekMoodCheckIns.length,
      weekStart: weekStartStr,
    };
  };

  // Theme unlock helpers
  const THEME_DEFINITIONS = [
    { id: 'dark', name: 'Dark Mode', unlockCondition: 'Default theme' },
    { id: 'system', name: 'System', unlockCondition: 'Default theme' },
    { id: 'ocean', name: 'Ocean Calm', unlockCondition: 'Reach Level 5' },
    { id: 'forest', name: 'Forest Focus', unlockCondition: 'Achieve 30-day streak' },
    { id: 'sunset', name: 'Sunset Glow', unlockCondition: 'Earn 10 achievements' },
  ];

  const unlockTheme = (themeId: string) => {
    dispatch({ type: 'UNLOCK_THEME', themeId });
  };

  const isThemeUnlocked = (themeId: string) => {
    return (state.progress.unlockedThemes || ['dark', 'system']).includes(themeId);
  };

  const checkAndUnlockThemes = () => {
    // Ocean theme: Level 5+
    if (state.progress.level >= 5 && !isThemeUnlocked('ocean')) {
      unlockTheme('ocean');
    }
    // Forest theme: 30-day streak
    if (state.progress.streak >= 30 && !isThemeUnlocked('forest')) {
      unlockTheme('forest');
    }
    // Sunset theme: 10+ achievements
    if (state.progress.achievements.length >= 10 && !isThemeUnlocked('sunset')) {
      unlockTheme('sunset');
    }
  };

  const getAvailableThemes = () => {
    return THEME_DEFINITIONS.map(theme => ({
      ...theme,
      unlocked: isThemeUnlocked(theme.id),
    }));
  };

  // Weekly challenge reward helpers
  const claimWeeklyChallenge = (challengeId: string, reward: number) => {
    dispatch({ type: 'CLAIM_WEEKLY_CHALLENGE', challengeId, reward });
  };

  const isWeeklyChallengeClaimed = (challengeId: string): boolean => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - dayOfWeek);
    weekStart.setHours(0, 0, 0, 0);
    const weekStartStr = weekStart.toISOString().split('T')[0];

    return (state.progress.claimedWeeklyChallenges || []).some(
      c => c.challengeId === challengeId && c.weekStart === weekStartStr
    );
  };

  // Engagement depth tracking
  const logEngagement = (type: string, data: Record<string, unknown>) => {
    dispatch({ type: 'LOG_ENGAGEMENT', entryType: type, data });
  };

  const getEngagementStats = () => {
    const engagementLog = state.progress.engagementLog || [];
    const reflections = engagementLog.filter(e => e.type === 'reflection_completed');
    const verifiedReflections = reflections.filter(e => (e.data.hasWrittenReflection as boolean) === true);
    const reflectionLengths = reflections
      .map(e => (e.data.reflectionLength as number) || 0)
      .filter(len => len > 0);
    const avgReflectionLength = reflectionLengths.length > 0
      ? Math.round(reflectionLengths.reduce((a, b) => a + b, 0) / reflectionLengths.length)
      : 0;
    const moodCheckInsWithContext = engagementLog.filter(
      e => e.type === 'mood_checkin_verified' && (e.data.hasContext as boolean) === true
    ).length;

    return {
      totalReflections: reflections.length,
      verifiedReflections: verifiedReflections.length,
      avgReflectionLength,
      moodCheckInsWithContext,
    };
  };

  // Get user's saved custom symptoms for reuse
  const getSavedCustomSymptoms = (): string[] => {
    return state.progress.savedCustomSymptoms || [];
  };

  // Goal management functions
  const createGoal = (
    title: string,
    type: GoalType,
    milestones: string[],
    description?: string,
    targetDate?: string
  ) => {
    const goal: Goal = {
      id: `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title,
      description,
      type,
      targetDate,
      milestones: milestones.map((m, index) => ({
        id: `milestone_${Date.now()}_${index}`,
        title: m,
        completed: false,
        points: 15, // Brain Points per milestone
      })),
      status: 'active',
      createdAt: new Date().toISOString(),
    };
    dispatch({ type: 'CREATE_GOAL', goal });
    dispatch({ type: 'EARN_POINTS', amount: 10 }); // Points for creating a goal
  };

  const updateGoal = (goalId: string, updates: Partial<Goal>) => {
    dispatch({ type: 'UPDATE_GOAL', goalId, updates });
  };

  const deleteGoal = (goalId: string) => {
    dispatch({ type: 'DELETE_GOAL', goalId });
  };

  const completeMilestone = (goalId: string, milestoneId: string) => {
    const goal = (state.progress.goals || []).find(g => g.id === goalId);
    const milestone = goal?.milestones.find(m => m.id === milestoneId);
    if (milestone && !milestone.completed) {
      dispatch({ type: 'COMPLETE_MILESTONE', goalId, milestoneId });
      dispatch({ type: 'EARN_POINTS', amount: milestone.points }); // Milestone points
    }
  };

  const completeGoalAction = (goalId: string, reflection?: string) => {
    dispatch({ type: 'COMPLETE_GOAL', goalId, reflection });
    dispatch({ type: 'EARN_POINTS', amount: 50 }); // Goal completion bonus

  };

  const pauseGoal = (goalId: string) => {
    dispatch({ type: 'PAUSE_GOAL', goalId });
  };

  const resumeGoal = (goalId: string) => {
    dispatch({ type: 'RESUME_GOAL', goalId });
  };

  const getActiveGoals = (): Goal[] => {
    return (state.progress.goals || []).filter(g => g.status === 'active');
  };

  const getCompletedGoals = (): Goal[] => {
    return (state.progress.goals || []).filter(g => g.status === 'completed');
  };

  const getGoalProgress = (goalId: string): { completed: number; total: number; percentage: number } => {
    const goal = (state.progress.goals || []).find(g => g.id === goalId);
    if (!goal) return { completed: 0, total: 0, percentage: 0 };
    const completed = goal.milestones.filter(m => m.completed).length;
    const total = goal.milestones.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  const getGoalSuggestions = (): { title: string; type: GoalType; milestones: string[] }[] => {
    const suggestions: { title: string; type: GoalType; milestones: string[] }[] = [];

    // Suggest based on user intent
    if (state.user.userIntent === 'manage-stress') {
      suggestions.push({
        title: 'Build Daily Stress Management Routine',
        type: 'wellness',
        milestones: [
          'Practice one breathing technique',
          'Complete 3 AWU sessions',
          'Log mood for 5 consecutive days',
          'Try 3 different coping techniques',
        ],
      });
    }

    // Suggest based on primary stressor
    if (state.user.primaryStressor === 'test-anxiety') {
      suggestions.push({
        title: 'Conquer Test Anxiety',
        type: 'academic',
        milestones: [
          'Learn about stress-as-fuel mindset',
          'Practice AWU before a quiz',
          'Create a pre-test routine',
          'Reflect on what worked after the test',
        ],
      });
    }

    if (state.user.primaryStressor === 'presentation') {
      suggestions.push({
        title: 'Become a Confident Presenter',
        type: 'social',
        milestones: [
          'Practice power poses',
          'Use AWU before speaking',
          'Present to one trusted person',
          'Speak up once in class',
        ],
      });
    }

    // Suggest based on upcoming event
    if (state.user.upcomingEvent?.name) {
      suggestions.push({
        title: `Prepare for ${state.user.upcomingEvent.name}`,
        type: 'academic',
        milestones: [
          'Create a preparation plan',
          'Practice stress-management technique',
          'Do a full AWU session',
          'Visualize success',
        ],
      });
    }

    // General suggestions for all users
    suggestions.push({
      title: 'Complete Resilience Foundations',
      type: 'skill',
      milestones: [
        'Finish "Your Brain is NOT Fixed" module',
        'Complete your first AWU session',
        'Try a breathing exercise',
        'Write your first reflection',
      ],
    });

    suggestions.push({
      title: 'Build a Growth Mindset Habit',
      type: 'personal',
      milestones: [
        'Learn about neuroplasticity',
        'Celebrate a struggle this week',
        'Reframe one negative thought',
        'Help someone else with a challenge',
      ],
    });

    return suggestions;
  };

  // Personal journaling helpers
  const addJournalEntry = (entry: Omit<JournalEntry, 'id' | 'date' | 'timestamp'>) => {
    dispatch({ type: 'ADD_JOURNAL_ENTRY', entry });
    // Writing is optional and does not change participation rewards.

  };

  const getJournalEntries = (): JournalEntry[] => {
    return (state.progress.journalEntries || []).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  };

  const getRecentJournalEntries = (limit = 5): JournalEntry[] => {
    return getJournalEntries().slice(0, limit);
  };

  const hasJournaledToday = (): boolean => {
    const today = new Date().toISOString().split('T')[0];
    return (state.progress.journalEntries || []).some(entry => entry.date === today);
  };

  // Spaced repetition / module progress helpers
  const logModuleCompletion = (moduleId: string) => {
    dispatch({ type: 'LOG_MODULE_COMPLETION', moduleId });
  };

  const completeRefresherAction = (moduleId: string, quizAccuracy?: number) => {
    dispatch({ type: 'COMPLETE_REFRESHER', moduleId, quizAccuracy });
    dispatch({ type: 'EARN_POINTS', amount: 20 }); // Refresher bonus
  };

  const getModuleProgress = (): ModuleProgressLog[] => {
    return state.progress.moduleProgress || [];
  };

  // Rewards shop helpers
  const purchaseShopItem = (itemId: string): boolean => {
    const item = getShopItem(itemId);
    if (!item) return false;
    if (state.progress.brainPoints < item.price) return false;
    if (item.levelRequired && state.progress.level < item.levelRequired) return false;

    const inventory = state.progress.inventory || {};
    const currentQty = inventory[itemId] || 0;

    // For non-consumable items, can only own 1
    if (!item.consumable && currentQty >= 1) return false;
    // Check max owned limit
    if (item.maxOwned !== undefined && currentQty >= item.maxOwned) return false;

    dispatch({ type: 'PURCHASE_ITEM', itemId, price: item.price });

    // Auto-unlock themes when purchased
    if (itemId === 'theme_neon' && !isThemeUnlocked('neon')) {
      unlockTheme('neon');
    }
    if (itemId === 'theme_retro' && !isThemeUnlocked('retro')) {
      unlockTheme('retro');
    }

    return true;
  };

  const useInventoryItem = (itemId: string): boolean => {
    const inventory = state.progress.inventory || {};
    const qty = inventory[itemId] || 0;
    if (qty <= 0) return false;
    dispatch({ type: 'USE_INVENTORY_ITEM', itemId });
    return true;
  };

  const getInventoryCount = (itemId: string): number => {
    return (state.progress.inventory || {})[itemId] || 0;
  };

  const canPurchaseItem = (itemId: string): boolean => {
    const item = getShopItem(itemId);
    if (!item) return false;
    if (state.progress.brainPoints < item.price) return false;
    if (item.levelRequired && state.progress.level < item.levelRequired) return false;
    const inventory = state.progress.inventory || {};
    const currentQty = inventory[itemId] || 0;
    if (!item.consumable && currentQty >= 1) return false;
    if (item.maxOwned !== undefined && currentQty >= item.maxOwned) return false;
    return true;
  };

  const isItemOwned = (itemId: string): boolean => {
    const item = getShopItem(itemId);
    if (!item || item.consumable) return false;
    return ((state.progress.inventory || {})[itemId] || 0) >= 1;
  };

  const getShopItemsList = (): ShopItem[] => {
    return SHOP_ITEMS;
  };

  const getTotalSpent = (): number => {
    return (state.progress.purchaseHistory || []).reduce((sum, p) => sum + p.price, 0);
  };

  // Assessment tracking helpers
  const logAssessmentResult = (result: AssessmentResult) => {
    dispatch({ type: 'LOG_ASSESSMENT_RESULT', result });
    // Award points for completing assessments
    const points = result.type === 'pre' ? 5 : 10;
    dispatch({ type: 'EARN_POINTS', amount: points });
  };

  const getAssessmentResults = (moduleId?: string): AssessmentResult[] => {
    const results = state.progress.assessmentResults || [];
    if (moduleId) {
      return results.filter(r => r.moduleId === moduleId);
    }
    return results;
  };

  const getModuleGrowth = (moduleId: string): {
    preScore: number | null;
    postScore: number | null;
    growth: number | null;
  } => {
    const results = state.progress.assessmentResults || [];
    const moduleResults = results.filter(r => r.moduleId === moduleId);
    // Get most recent pre and post scores
    const preResults = moduleResults.filter(r => r.type === 'pre');
    const postResults = moduleResults.filter(r => r.type === 'post');
    const latestPre = preResults.length > 0 ? preResults[preResults.length - 1] : null;
    const latestPost = postResults.length > 0 ? postResults[postResults.length - 1] : null;

    return {
      preScore: latestPre?.score ?? null,
      postScore: latestPost?.score ?? null,
      growth: latestPre && latestPost ? latestPost.score - latestPre.score : null,
    };
  };

  const getOverallGrowth = (): {
    avgPreScore: number;
    avgPostScore: number;
    avgGrowth: number;
    modulesAssessed: number;
  } => {
    const results = state.progress.assessmentResults || [];
    // Group by module, get latest pre/post for each
    const moduleMap = new Map<string, { pre: number | null; post: number | null }>();
    for (const r of results) {
      const entry = moduleMap.get(r.moduleId) ?? { pre: null, post: null };
      if (r.type === 'pre') entry.pre = r.score;
      if (r.type === 'post') entry.post = r.score;
      moduleMap.set(r.moduleId, entry);
    }

    const modulesWithBoth = Array.from(moduleMap.values()).filter(
      m => m.pre !== null && m.post !== null
    );

    if (modulesWithBoth.length === 0) {
      return { avgPreScore: 0, avgPostScore: 0, avgGrowth: 0, modulesAssessed: 0 };
    }

    const avgPre = modulesWithBoth.reduce((sum, m) => sum + (m.pre ?? 0), 0) / modulesWithBoth.length;
    const avgPost = modulesWithBoth.reduce((sum, m) => sum + (m.post ?? 0), 0) / modulesWithBoth.length;

    return {
      avgPreScore: Math.round(avgPre),
      avgPostScore: Math.round(avgPost),
      avgGrowth: Math.round(avgPost - avgPre),
      modulesAssessed: modulesWithBoth.length,
    };
  };

  // === Psychological Framework Helpers ===

  // SRL helpers
  const logSRLSession = (session: SRLSession) => {
    dispatch({ type: 'LOG_SRL_SESSION', session });

  };

  const getSRLSessions = (): SRLSession[] => {
    return state.progress.srlSessions || [];
  };

  const getSRLCompletionRate = (): number => {
    const sessions = state.progress.srlSessions || [];
    if (sessions.length === 0) return 0;
    const fullCycles = sessions.filter(s => s.completedFullCycle).length;
    return Math.round((fullCycles / sessions.length) * 100);
  };

  // Metacognition helpers
  const logStrategyUsage = (usage: StrategyUsage) => {
    dispatch({ type: 'LOG_STRATEGY_USAGE', usage });
    dispatch({ type: 'EARN_POINTS', amount: 2 }); // Strategy tracking bonus
  };

  const getStrategyEffectiveness = () => {
    const usages = state.progress.strategyUsage || [];
    const grouped = new Map<string, { total: number; effective: number }>();

    for (const usage of usages) {
      const existing = grouped.get(usage.strategyId) ?? { total: 0, effective: 0 };
      existing.total++;
      if (usage.outcome === 'effective') existing.effective++;
      grouped.set(usage.strategyId, existing);
    }

    return Array.from(grouped.entries()).map(([strategyId, data]) => ({
      strategyId,
      uses: data.total,
      effectiveRate: data.total > 0 ? Math.round((data.effective / data.total) * 100) : 0,
    }));
  };

  const updatePreferredStrategies = (strategies: string[]) => {
    dispatch({ type: 'UPDATE_PREFERRED_STRATEGIES', strategies });
  };

  // Executive Function helpers
  const logEFGame = (session: Omit<EFGameSession, 'personalBest'>) => {
    void trackEvent({event_name: 'ef_game_completed', payload: {game_id: session.gameId, score: session.score}});
    dispatch({ type: 'LOG_EF_GAME', session: { ...session, personalBest: false } });
    // Points based on performance (10-30 GP)
    const points = Math.min(30, Math.max(10, Math.round(session.score / 10)));
    dispatch({ type: 'EARN_POINTS', amount: points });
    // Personal best bonus
    const currentBest = (state.progress.efPersonalBests || {})[session.gameId] ?? 0;
    if (session.score > currentBest) {
      dispatch({ type: 'EARN_POINTS', amount: 10 }); // Personal best bonus
    }
  };

  const getEFPersonalBests = (): Record<string, number> => {
    return state.progress.efPersonalBests || {};
  };

  const getEFGameHistory = (gameId: string): EFGameSession[] => {
    return (state.progress.efGameSessions || []).filter(s => s.gameId === gameId);
  };

  const getAllEFGameSessions = (): EFGameSession[] => {
    return state.progress.efGameSessions || [];
  };

  // ZPD / Scaffolding helpers
  const updateSkillProfile = (skillId: SkillDimension, level: number) => {
    dispatch({ type: 'UPDATE_SKILL_PROFILE', skillId, level });
  };

  const getSkillProfiles = (): SkillProfile[] => {
    return state.progress.skillProfiles || [];
  };

  const getScaffoldLevel = (activityType: string): ScaffoldLevel => {
    return (state.progress.scaffoldLevels || {})[activityType] ?? 'full';
  };

  const updateScaffoldLevelAction = (activityType: string, level: ScaffoldLevel) => {
    dispatch({ type: 'UPDATE_SCAFFOLD_LEVEL', activityType, level });
  };

  // SDT helpers
  const logAutonomyChoice = (choice: string, context: string) => {
    dispatch({ type: 'LOG_AUTONOMY_CHOICE', choice, context });
  };

  const updateCommunityPulse = (pulse: CommunityPulse) => {
    dispatch({ type: 'UPDATE_COMMUNITY_PULSE', pulse });
  };

  const getCommunityPulse = (): CommunityPulse | null => {
    return state.progress.communityPulse || null;
  };

  const setCustomPractice = (practice: CustomPractice | null) => {
    dispatch({ type: 'SET_CUSTOM_PRACTICE', practice });
  };

  const getCustomPractice = (): CustomPractice | null => {
    return state.progress.customPractice || null;
  };

  // Feature tour helpers
  const completeFeatureTour = () => {
    dispatch({ type: 'COMPLETE_FEATURE_TOUR' });
  };

  const dismissFeatureTour = () => {
    dispatch({ type: 'DISMISS_FEATURE_TOUR' });
  };

  const resetFeatureTour = () => {
    dispatch({ type: 'RESET_FEATURE_TOUR' });
  };

  const shouldShowFeatureTour = (): boolean => {
    return state.onboardingComplete && !state.featureTourComplete && !state.featureTourDismissed;
  };

  // Check for theme unlocks when progress changes
  useEffect(() => {
    if (state.onboardingComplete) {
      checkAndUnlockThemes();
    }
  }, [state.progress.level, state.progress.streak, state.progress.achievements.length]);

  return (
    <AppContext.Provider
      value={{
        state,
        dispatch,
        earnPoints,
        completeModule,
        completeAWU,
        getNextLevelThreshold,
        getProgressToNextLevel,
        checkIn,
        hasCheckedInToday,
        completeOnboarding,
        resetProgress,
        unlockAchievement,
        setAchievementNotifyCallback,
        checkAndUnlockAchievements,
        logStress,
        addReflection,
        updateQuizStats,
        setFuelLevel,
        useStreakFreeze,
        canUseStreakFreeze,
        completeDailyChallenge,
        isDailyChallengeCompleted,
        getQuizAccuracy,
        logMoodCheckIn,
        hasMoodCheckedInToday,
        getTodaysMood,
        logTechniqueSession,
        getTechniqueStats,
        getTopTechnique,
        getWeeklyChallengeProgress,
        unlockTheme,
        isThemeUnlocked,
        checkAndUnlockThemes,
        getAvailableThemes,
        claimWeeklyChallenge,
        isWeeklyChallengeClaimed,
        logEngagement,
        getEngagementStats,
        getSavedCustomSymptoms,
        createGoal,
        updateGoal,
        deleteGoal,
        completeMilestone,
        completeGoal: completeGoalAction,
        pauseGoal,
        resumeGoal,
        getActiveGoals,
        getCompletedGoals,
        getGoalProgress,
        getGoalSuggestions,
        addJournalEntry,
        getJournalEntries,
        getRecentJournalEntries,
        hasJournaledToday,
        logModuleCompletion,
        completeRefresher: completeRefresherAction,
        getModuleProgress,
        purchaseShopItem,
        useInventoryItem,
        getInventoryCount,
        canPurchase: canPurchaseItem,
        isItemOwned,
        getShopItems: getShopItemsList,
        getTotalSpent,
        logAssessmentResult,
        getAssessmentResults,
        getModuleGrowth,
        getOverallGrowth,
        // Psychological framework helpers
        logSRLSession,
        getSRLSessions,
        getSRLCompletionRate,
        logStrategyUsage,
        getStrategyEffectiveness,
        updatePreferredStrategies,
        logEFGame,
        getEFPersonalBests,
        getEFGameHistory,
        getAllEFGameSessions,
        updateSkillProfile,
        getSkillProfiles,
        getScaffoldLevel,
        updateScaffoldLevel: updateScaffoldLevelAction,
        logAutonomyChoice,
        updateCommunityPulse,
        getCommunityPulse,
        setCustomPractice,
        getCustomPractice,
        // Feature tour helpers
        completeFeatureTour,
        dismissFeatureTour,
        resetFeatureTour,
        shouldShowFeatureTour,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}

// Export types for use in other components
export type {
  UserProfile, UserIntent, PrimaryStressor, Goal, GoalType, Milestone, JournalEntry, ModuleProgressLog, ShopPurchase,
  // Psychological framework types
  SRLSession, SRLForethought, SRLPerformanceMonitoring, SRLReflection, SRLActivityType,
  StrategyUsage, EFGameSession, SkillProfile, SkillDimension, ScaffoldLevel,
  AutonomyChoice, CommunityPulse, CustomPractice,
};

